import { useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { emailRe, errorFields, errorText, submitLead, LEAD_FIELD_ERRORS } from "@/lib/leads";

const FIELDS = [
  { key: "prenom", label: "Prénom", type: "text", placeholder: "Marie", autoComplete: "given-name" },
  { key: "nom", label: "Nom", type: "text", placeholder: "Durand", autoComplete: "family-name" },
  { key: "email", label: "Email", type: "email", placeholder: "marie@email.com", autoComplete: "email" },
  { key: "telephone", label: "Téléphone", type: "tel", placeholder: "06 12 34 56 78", autoComplete: "tel", optional: true },
];

const ctaClass =
  "h-[56px] w-full bg-[#C84C2C] text-white rounded-full font-semibold shadow-[0_8px_20px_-6px_rgba(200,76,44,0.55)] transition-transform active:scale-[0.98] hover:bg-[#B23E20] disabled:opacity-60";

function Field({ f, value, error, onChange }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#231C18] mb-1.5">
        {f.label}
        {f.optional && <span className="ml-1 font-normal text-[#8C7A70]">(facultatif)</span>}
      </label>
      <input
        data-testid={`unlock-input-${f.key}`}
        type={f.type}
        autoComplete={f.autoComplete}
        value={value}
        onChange={(e) => onChange(f.key, e.target.value)}
        placeholder={f.placeholder}
        className={`h-[52px] w-full bg-white border rounded-2xl px-4 text-[#231C18] text-base font-medium outline-none transition-all placeholder-[#8C7A70]/60 ${
          error ? "border-[#C62828] ring-1 ring-[#C62828]" : "border-[#E6DDD4] focus:border-[#C84C2C] focus:ring-4 focus:ring-[#C84C2C]/15"
        }`}
      />
      {error && (
        <p className="mt-1.5 text-xs font-medium text-[#C62828]" data-testid={`unlock-error-${f.key}`}>
          {LEAD_FIELD_ERRORS[f.key]}
        </p>
      )}
      {f.optional && (
        <p className="mt-1.5 text-xs text-[#8C7A70]" data-testid="unlock-phone-hint">
          Facultatif — uniquement si vous souhaitez qu'un expert vous rappelle.
        </p>
      )}
    </div>
  );
}

// Bandeau + formulaire en place sur la page résultat : débloque le détail après envoi
export function UnlockForm({ sim, prepare, onUnlocked }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", telephone: "", consentement: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: false }));
  };

  const validate = () => {
    const e = {};
    if (!form.prenom.trim()) e.prenom = true;
    if (!form.nom.trim()) e.nom = true;
    if (!emailRe.test(form.email.trim())) e.email = true;
    const digits = form.telephone.replace(/\D/g, "");
    if (digits && digits.length < 8) e.telephone = true;
    if (!form.consentement) e.consentement = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Merci de vérifier vos informations.");
      return;
    }
    prepare();
    setLoading(true);
    try {
      await submitLead("/simulations/send-result", form, sim);
      toast.dismiss();
      onUnlocked(form.email.trim());
    } catch (e) {
      const fields = errorFields(e);
      if (fields) setErrors((prev) => ({ ...prev, ...Object.fromEntries(fields.map((f) => [f, true])) }));
      toast.error(errorText(fields));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FFFFFF] border-2 border-[#C84C2C]/25 rounded-2xl p-5 shadow-[0_12px_30px_-12px_rgba(200,76,44,0.35)] animate-fade-up" data-testid="unlock-banner">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 shrink-0 rounded-full bg-[#C84C2C]/10 flex items-center justify-center">
          <Lock strokeWidth={1.75} className="h-4 w-4 text-[#C84C2C]" />
        </span>
        <p className="text-sm text-[#231C18] leading-relaxed" data-testid="unlock-banner-text">
          Recevez l'analyse complète par email : projection à 10 ans, TRI, tableau comparatif, hypothèses détaillées.
        </p>
      </div>

      {open && (
        <div className="mt-5 space-y-4" data-testid="unlock-form">
          {FIELDS.map((f) => (
            <Field key={f.key} f={f} value={form[f.key]} error={errors[f.key]} onChange={set} />
          ))}
          <label className="flex items-start gap-3 cursor-pointer" data-testid="unlock-consent-label">
            <input
              type="checkbox"
              data-testid="unlock-consent-checkbox"
              checked={form.consentement}
              onChange={(e) => set("consentement", e.target.checked)}
              className={`mt-0.5 h-5 w-5 shrink-0 accent-[#C84C2C] ${errors.consentement ? "outline outline-2 outline-[#C62828]" : ""}`}
            />
            <span className={`text-xs leading-relaxed ${errors.consentement ? "text-[#C62828]" : "text-[#8C7A70]"}`}>
              J'accepte de recevoir mon analyse par email et d'être recontacté au sujet de mon projet immobilier.{" "}
              <a
                href="https://www.assertimmo.com/mentions-legales-assertimmo"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="unlock-privacy-link"
                className="text-[#C84C2C] font-semibold underline underline-offset-2"
              >
                Politique de confidentialité
              </a>
            </span>
          </label>
        </div>
      )}

      <button
        data-testid={open ? "unlock-submit-btn" : "result-cta-recevoir"}
        onClick={open ? submit : () => setOpen(true)}
        disabled={loading}
        className={`mt-5 ${ctaClass}`}
      >
        {loading ? "Envoi en cours…" : "Recevoir l'analyse complète"}
      </button>
    </div>
  );
}
